import { useCallback, useEffect, useRef, useState } from 'react'

import { get, set } from 'local-storage'
import { clamp } from 'lodash'

type Values = [number, (e: React.MouseEvent<HTMLElement>) => void, () => void]

export const useResizeable = (
  min: number,
  max: number,
  initial?: number,
  localstorageKey?: string
): Values => {
  const lsWidth = localstorageKey ? get<number>(localstorageKey) : undefined
  const isDragging = useRef<boolean>(false)
  const [width, setWidth] = useState(lsWidth || initial || min)

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging.current) {
        const width = clamp(e.clientX, min, max) // this assumes hard up against left side of screen
        setWidth(width)

        if (localstorageKey) {
          set(localstorageKey, width)
        }
      }
    },
    [min, max, localstorageKey]
  )

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      isDragging.current = true
      document.addEventListener('mousemove', onMouseMove)
    },
    [onMouseMove]
  )

  const onMouseUp = useCallback(() => {
    isDragging.current = false
    document.removeEventListener('mouseup', onMouseMove)
  }, [onMouseMove])

  const onDoubleClick = useCallback(() => {
    const width = initial || min
    setWidth(width)

    if (localstorageKey) {
      set(localstorageKey, width)
    }
  }, [initial, min, localstorageKey])

  useEffect(() => {
    document.addEventListener('mouseup', onMouseUp)

    return () => {
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [onMouseUp])

  return [width, onMouseDown, onDoubleClick]
}
